/*
 * Ticket detail (seats) — `orders.ticketdetail` (App-shell canon §7.2,
 * FLEXIBLE). Transcribed from the canon §7.2 + testid table + the DC source
 * (App Shell.dc.html seat()). Canon wins on conflict.
 *
 * ROUTE (canon §1.2): /orders/:orderRef/tickets/:ticketId. Seat actions open
 * /orders/:orderRef/tickets/:ticketId/seats/:seatNo/assign (§7.3).
 *
 * DATA (ruling A): live box-office ticket + my-api holders via useOrderSeats;
 * honest notice while the allocation service is unreadable. A DONE ticket
 * still exposes `Manage` — re-assignment stays possible until check-in
 * (§7.2 must-not).
 */
import { useNavigate, useParams } from 'react-router-dom'
import { LiveDataNotice, useData } from '../../data/DataProvider'
import type { Ticket } from '../../data/types'
import { AllocNotice } from '../Orders'
import {
  peopleLabel, seatView, ticketStatusPill, type TicketDotState,
} from './ordersModel'
import { useOrderSeats } from './useOrderSeats'

const PILL_FG: Record<TicketDotState, string> = {
  need: 'var(--warn)',
  pending: 'var(--info-invited)',
  done: 'var(--accent-bright)',
}

// ---------------------------------------------------------------------------
// Presentational (tests render it with the seed fixture)
// ---------------------------------------------------------------------------

export function TicketSeats({ ticket, onBack, onOpenSeat }: {
  ticket: Ticket
  onBack?: () => void
  /** Both Manage and Please-assign open the assign flow for that seat (§7.2). */
  onOpenSeat?: (seatNo: number) => void
}) {
  const pill = ticketStatusPill(ticket)
  return (
    <div id="ticketdetail.root">
      <button
        data-testid="ticketdetail-back"
        onClick={onBack}
        style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '0 0 14px', minHeight: 44 }}
      >‹ All tickets</button>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline-08)', borderRadius: 16, overflow: 'hidden' }}>
        {/* header: type + status pill + mono people count */}
        <div id="ticketdetail.head" style={{ padding: '16px 18px', borderBottom: '1px solid var(--hairline-07)', background: 'var(--bg-frame)' }}>
          <div data-testid="ticketdetail-title" style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em' }}>{ticket.typeName}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 6 }}>
            <span
              data-testid="ticketdetail-status"
              data-state={pill.dot}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 800, color: PILL_FG[pill.dot] }}
            >{pill.text}</span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--text-faint)' }}>{`· ${peopleLabel(ticket)}`}</span>
          </div>
        </div>

        {/* seat blocks */}
        {ticket.seats.map((s) => {
          const v = seatView(ticket, s)
          return (
            <div
              key={s.seatNo}
              id={`ticketdetail.seat.${ticket.id}.${s.seatNo}`}
              data-testid="orders-seat"
              data-seat={`${ticket.id}.${s.seatNo}`}
              style={{ padding: '14px 18px', borderBottom: '1px solid var(--hairline-06)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
                {/* numbered roundel: teal filled / amber empty (§7.2) */}
                <span
                  aria-hidden
                  className="mono"
                  style={{
                    width: 24, height: 24, flex: 'none', borderRadius: '50%', display: 'grid', placeItems: 'center',
                    fontSize: 10.5, fontWeight: 700,
                    ...(v.filled
                      ? { background: 'rgba(18,196,172,.2)', color: 'var(--success)' }
                      : { background: 'rgba(230,193,90,.18)', color: 'var(--warn)' }),
                  }}
                >{v.num}</span>
                <span className="mono" style={{ fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-faint)' }}>{v.personLabel}</span>
                {v.isMine && (
                  <span data-testid="orders-seat-you" style={{ flex: 'none', fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 20, background: 'rgba(63,224,200,.18)', color: 'var(--accent-bright)' }}>You</span>
                )}
              </div>
              <div
                data-testid="orders-seat-holder"
                style={{ fontSize: 14, fontWeight: 700, overflowWrap: 'anywhere', marginBottom: 6, color: v.filled ? 'var(--text)' : 'var(--warn)' }}
              >{v.who}</div>
              {v.sub && (
                <div
                  data-testid="orders-seat-sub"
                  style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 11, color: v.subTone === 'invited' ? 'var(--info-invited)' : 'var(--text-muted)' }}
                >{v.sub}</div>
              )}
              <div style={{ height: 5 }} />
              {v.filled ? (
                <button
                  data-testid="orders-seat-manage"
                  onClick={() => onOpenSeat?.(s.seatNo)}
                  style={{ width: '100%', border: '1px solid var(--hairline-16)', borderRadius: 10, minHeight: 44, background: 'transparent', color: 'var(--text)', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}
                >Manage</button>
              ) : (
                <button
                  data-testid="orders-seat-assign"
                  onClick={() => onOpenSeat?.(s.seatNo)}
                  style={{ width: '100%', border: 0, borderRadius: 10, minHeight: 44, background: 'var(--accent)', color: 'var(--accent-ink)', fontSize: 13, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer' }}
                >Please assign this ticket</button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Live route container
// ---------------------------------------------------------------------------

export function TicketDetail() {
  const { liveState, orders } = useData()
  const { orderRef = '', ticketId = '' } = useParams()
  const navigate = useNavigate()
  const order = orders.find((o) => o.ref === orderRef) ?? null
  const { state, tickets } = useOrderSeats(order)

  if (liveState !== 'ready') {
    return <div id="ticketdetail.root" style={{ maxWidth: 'var(--content-max, 700px)' }}><LiveDataNotice liveState={liveState} /></div>
  }
  const ticket = tickets.find((t) => t.id === ticketId) ?? null
  if (!order || !ticket) {
    // Honest miss (bad deep link / data not present) — back to the list.
    return (
      <div id="ticketdetail.root" style={{ maxWidth: 'var(--content-max, 700px)' }}>
        <button
          data-testid="ticketdetail-back"
          onClick={() => navigate('/orders')}
          style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '0 0 14px', minHeight: 44 }}
        >‹ All tickets</button>
        <p role="status" className="secondary" style={{ fontSize: 13 }}>
          That ticket isn&rsquo;t in your orders on this account.
        </p>
      </div>
    )
  }
  return (
    <div style={{ maxWidth: 'var(--content-max, 700px)' }}>
      {state !== 'ready' && (
        // D11: same state-accurate split as the list card — signed-out shows a
        // sign-in affordance, never the build-gap copy.
        <AllocNotice state={state} style={{ padding: 0, margin: '0 0 10px' }} />
      )}
      <TicketSeats
        ticket={ticket}
        onBack={() => navigate(`/orders/${orderRef}`)}
        onOpenSeat={state === 'ready'
          ? (seatNo) => navigate(`/orders/${orderRef}/tickets/${ticketId}/seats/${seatNo}/assign`)
          : undefined}
      />
    </div>
  )
}
